import { creativeErrorMessage } from '@/utils/creative-error-message'
import { memo, useState, useEffect, useCallback, useContext } from 'react'
import { useLazyQuery, useMutation, useSubscription } from '@apollo/client'
import useAdCreativesStore, {
    isCreativeEligibleForSelection,
    hasVisibleAssetVariations,
} from '../../../store/adCreativesStore'
import {
    GET_AD_CREATIVES_WITH_ASSETS,
    DELETE_AD_CREATIVE,
    AD_CREATIVE_STATUS_UPDATE,
} from '../../../graphql/ad-creatives-library-queries'
import { Header, FilterSection, CreativesList, Footer } from './components'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { useParams } from 'react-router-dom'
import { showNotification } from '@/utils/notifications'
import { toast } from 'react-toastify'
import { AuthContext } from '@/context/auth-context'

function formatAdCreativeSubscriptionError(error) {
    return creativeErrorMessage(error) || 'Ad creative update failed. Please try again.'
}

const AdLibrary = memo(() => {
    const {
        isLoading,
        searchTerm,
        filterFormats,
        error,
        setCreatives,
        setLoading,
        setError,
        toggleCreativeSelection,
        clearSelection,
        selectAllEligibleCreatives,
        setSearchTerm,
        toggleFormatFilter,
        clearFormatFilters,
        deleteCreative,
        getFilteredCreatives,
        getSelectionCount,
        getEligibleSelectionCount,
        isCreativeSelected,
        hasActiveFilters,
    } = useAdCreativesStore()

    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)
    const [hasInitialLoad, setHasInitialLoad] = useState(false)
    const { connectedPublishers } = useMediaBuyingContext()
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : null
    const { id: campaignId } = useParams()
    const { user } = useContext(AuthContext)

    const [fetchCreatives, { loading: queryLoading }] = useLazyQuery(GET_AD_CREATIVES_WITH_ASSETS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            const creativesData = data?.AdCreative?.searchFilterAdCreativesJoinAssets || []
            setCreatives(creativesData)
            setLoading(false)
            setError(null) // Clear any previous errors
        },
        onError: (error) => {
            setError('Failed to load creatives. Please try again.')
            setLoading(false)
        },
    })

    const [deleteCreativeMutation] = useMutation(DELETE_AD_CREATIVE, {
        onError: (error) => {
            // Use the actual error message from the resolver
            const errorMessage = error.graphQLErrors?.[0]?.message
            showNotification(errorMessage, 'error', 3000, null, null, false, true)
        },
        onCompleted: () => {
            toast.success('Creative deleted successfully')
        },
    })

    // Subscribe to ad creative status updates
    useSubscription(AD_CREATIVE_STATUS_UPDATE, {
        variables: { userId: user?.id },
        skip: !user?.id,
        onData: ({ data: subscriptionData }) => {
            if (subscriptionData?.data?.adCreativeStatusUpdate) {
                const updatedCreative = subscriptionData.data.adCreativeStatusUpdate

                // For PUBLISHED and FAILED statuses, update cache directly instead of refetching
                if (updatedCreative.status === 'PUBLISHED' || updatedCreative.status === 'FAILED') {
                    if (updatedCreative.status === 'PUBLISHED') {
                        showNotification('Ad creative saved successfully!', 'success', 3000, null, null, true, true)
                    } else {
                        showNotification(
                            formatAdCreativeSubscriptionError(updatedCreative.error),
                            'error',
                            8000,
                            null,
                            null,
                            true,
                            true,
                        )
                    }
                    // Refetch full list so name, tags, and copy match the server (partial merge was stale after edits,
                    // especially after used-creative Meta republish which only sends a subset of fields).
                    const formats = filterFormats.size > 0 ? Array.from(filterFormats) : null
                    const searchQuery = searchTerm.length >= 2 ? searchTerm : null
                    fetchCreatives({
                        variables: {
                            searchTerm: searchQuery,
                            formats: formats,
                            campaignId,
                        },
                    })
                } else {
                    // For other statuses (PROCESSING, etc.), refetch as before
                    const formats = filterFormats.size > 0 ? Array.from(filterFormats) : null
                    const searchQuery = searchTerm.length >= 2 ? searchTerm : null

                    fetchCreatives({
                        variables: {
                            searchTerm: searchQuery,
                            formats: formats,
                            campaignId,
                        },
                    })
                }
            }
        },
        onError: (error) => {
            console.error('Subscription error:', error)
        },
    })

    // Debounced search effect - only run after initial load
    useEffect(() => {
        // Skip if this is the initial load (no filters yet)
        if (!hasInitialLoad) {
            return
        }

        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer)
        }

        const timer = setTimeout(() => {
            const formats = filterFormats.size > 0 ? Array.from(filterFormats) : null
            const searchQuery = searchTerm.length >= 2 ? searchTerm : null

            setError(null) // Clear any previous errors
            fetchCreatives({
                variables: {
                    searchTerm: searchQuery,
                    formats: formats,
                    campaignId,
                },
            })
        }, 300)

        setSearchDebounceTimer(timer)

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [searchTerm, filterFormats, fetchCreatives, setError, hasInitialLoad, campaignId])

    // Initial load
    useEffect(() => {
        if (!hasInitialLoad) {
            setLoading(true)
            setError(null) // Clear any previous errors
            fetchCreatives({
                variables: {
                    searchTerm: null,
                    formats: null,
                    campaignId,
                },
            })
            setHasInitialLoad(true)
        }
    }, [hasInitialLoad, fetchCreatives, setLoading, setError, campaignId])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceTimer) {
                clearTimeout(searchDebounceTimer)
            }
        }
    }, [searchDebounceTimer])

    const handleSearchChange = useCallback(
        (value) => {
            setSearchTerm(value)
            // Set loading immediately to prevent showing filtered results from stale data
            if (hasInitialLoad) {
                setLoading(true)
            }
        },
        [setSearchTerm, setLoading, hasInitialLoad],
    )

    const handleDeleteCreative = useCallback(
        async (id) => {
            await deleteCreative(id, async (creativeId) => {
                const result = await deleteCreativeMutation({
                    variables: { id: creativeId, publisherId: publisherId, campaignId: campaignId },
                })
                if (result?.errors?.message) {
                    throw new Error(result.errors.message)
                }
            })
        },
        [deleteCreative, deleteCreativeMutation, publisherId, campaignId],
    )

    const handleClearSelection = useCallback(() => {
        clearSelection()
    }, [clearSelection])

    const handleSelectAll = useCallback(() => {
        selectAllEligibleCreatives()
    }, [selectAllEligibleCreatives])

    const handleClearFilters = useCallback(() => {
        setSearchTerm('')
        clearFormatFilters()
        // Set loading immediately to prevent showing filtered results from stale data
        if (hasInitialLoad) {
            setLoading(true)
        }
    }, [setSearchTerm, clearFormatFilters, setLoading, hasInitialLoad])

    const handleFormatToggle = useCallback(
        (format) => {
            toggleFormatFilter(format)
            // Set loading immediately to prevent showing filtered results from stale data
            if (hasInitialLoad) {
                setLoading(true)
            }
        },
        [toggleFormatFilter, setLoading, hasInitialLoad],
    )

    const handleClearFormatFilters = useCallback(() => {
        clearFormatFilters()
        // Set loading immediately to prevent showing filtered results from stale data
        if (hasInitialLoad) {
            setLoading(true)
        }
    }, [clearFormatFilters, setLoading, hasInitialLoad])

    const handleApplyFilters = useCallback(
        (newFormats) => {
            // Apply the new format filters more efficiently
            // Instead of clearing and toggling multiple times, update the store directly
            useAdCreativesStore.setState({ filterFormats: new Set(newFormats) })
            // Set loading immediately to prevent showing filtered results from stale data
            if (hasInitialLoad) {
                setLoading(true)
            }
        },
        [setLoading, hasInitialLoad],
    )

    const filteredCreatives = getFilteredCreatives()
    const selectionCount = getSelectionCount()
    const eligibleSelectionCount = getEligibleSelectionCount()
    const eligibleCount = filteredCreatives.filter(
        (c) => isCreativeEligibleForSelection(c) && hasVisibleAssetVariations(c),
    ).length
    const hasFilters = hasActiveFilters()
    const isLoadingState = isLoading || queryLoading

    return (
        <div className="tw:bg-[#071727] tw:h-full tw:text-[#c9d1d9] tw:flex tw:flex-col tw:overflow-hidden tw:relative">
            <div className="tw:flex-shrink-0">
                <Header />
            </div>
            <div className="tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden tw:min-h-0">
                <div className="tw:flex-shrink-0 tw:p-6">
                    <FilterSection
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        filterFormats={filterFormats}
                        onFormatToggle={handleFormatToggle}
                        onClearFormatFilters={handleClearFormatFilters}
                        onApplyFilters={handleApplyFilters}
                        selectionCount={selectionCount}
                        onClearSelection={handleClearSelection}
                        showSelectAll={eligibleCount > 0}
                        onSelectAll={handleSelectAll}
                    />
                </div>

                <div className="tw:flex-1 tw:overflow-auto tw:px-6 tw:min-h-0">
                    <CreativesList
                        isLoading={isLoadingState}
                        error={error}
                        filteredCreatives={filteredCreatives}
                        hasActiveFilters={hasFilters}
                        isCreativeSelected={isCreativeSelected}
                        onToggleSelection={toggleCreativeSelection}
                        onDeleteCreative={handleDeleteCreative}
                    />
                </div>
            </div>

            <Footer selectionCount={eligibleSelectionCount} onClearSelection={handleClearSelection} />
        </div>
    )
})

export default AdLibrary
